import React from "react";

const Section = ({ n, title, children }) => (
  <section className="grid grid-cols-12 gap-6 py-8 border-t border-black/10">
    <div className="col-span-12 md:col-span-3">
      <div className="font-mono-eyebrow">{n}</div>
      <h3 className="font-serif-display text-2xl mt-1 leading-tight">{title}</h3>
    </div>
    <div className="col-span-12 md:col-span-9 text-black/70 leading-relaxed space-y-4">{children}</div>
  </section>
);

export const Privacy = () => (
  <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-16">
    <div className="font-mono-eyebrow">Legal · Draft</div>
    <h1 className="font-serif-display text-6xl md:text-7xl leading-none mt-3">Privacy Policy</h1>
    <p className="mt-6 text-black/60 max-w-2xl">
      Last updated: {new Date().toLocaleDateString(undefined, {year: "numeric", month: "long", day: "numeric"})}.
      This is a plain-language starter policy for Atelier. It is not a substitute for legal advice — please have a lawyer review before public launch.
    </p>
    <Section n="01" title="What we collect">
      <p>To make Atelier work, we store: your email &amp; hashed password, the portraits you upload, the product URLs and images you add to your wardrobe, the outfits you compose, your body measurements (height, size, fit preference — all optional), and analytics on how you use the app (page views, feature usage, ad impressions/clicks).</p>
    </Section>
    <Section n="02" title="How we use it">
      <p>Your portraits and wardrobe items are only used to generate your virtual try-on results and to display them to you. We never share your portraits with other members. Outfits you mark as public may be shown to other members and search engines — everything else stays private.</p>
    </Section>
    <Section n="03" title="Third parties">
      <p>Atelier uses a handful of trusted services to operate: <strong>Stripe</strong> (payments), <strong>RevenueCat</strong> (mobile subscriptions), <strong>Google Gemini</strong> and <strong>Anthropic Claude</strong> (AI image + text generation), and <strong>MongoDB Atlas</strong> (data hosting). Each has their own privacy policy. Your portrait is sent to Google for the try-on generation and is not retained beyond the request.</p>
    </Section>
    <Section n="04" title="Your rights (GDPR / UK GDPR)">
      <p>You can request a copy of your data, correct any errors, or ask for your account and content to be deleted at any time. Email <span className="font-mono">privacy@atelier.example</span> (replace with your real address) and we'll respond within 30 days.</p>
    </Section>
    <Section n="05" title="Cookies">
      <p>Atelier stores a session token in your browser's local storage to keep you signed in. We do not use third-party advertising or tracking cookies.</p>
    </Section>
    <Section n="06" title="Children">
      <p>Atelier is not intended for anyone under 16. We do not knowingly collect data from children.</p>
    </Section>
    <Section n="07" title="Changes">
      <p>If we change this policy in any meaningful way, we'll email registered members and post a notice inside the app before the change takes effect.</p>
    </Section>
    <div className="pt-12 text-sm text-black/40 border-t border-black/10 mt-6">
      © {new Date().getFullYear()} Atelier. All rights reserved.
    </div>
  </div>
);

export const Terms = () => (
  <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-16">
    <div className="font-mono-eyebrow">Legal · Draft</div>
    <h1 className="font-serif-display text-6xl md:text-7xl leading-none mt-3">Terms of Service</h1>
    <p className="mt-6 text-black/60 max-w-2xl">
      Last updated: {new Date().toLocaleDateString(undefined, {year: "numeric", month: "long", day: "numeric"})}.
      A friendly, plain-English contract between you and Atelier. Have a lawyer finalise before public launch.
    </p>
    <Section n="01" title="Your account">
      <p>You must be at least 16 to use Atelier. Keep your password confidential — you're responsible for activity on your account. You can close your account at any time from Profile.</p>
    </Section>
    <Section n="02" title="Subscription &amp; billing">
      <p>Atelier offers a 30-day free trial (no card required) followed by a £9.99 / year subscription. On the web, payment is processed by Stripe. On iOS the subscription is billed by Apple; on Android by Google Play. You can cancel any time — access continues until the end of the paid period.</p>
      <p>Refunds outside statutory rights are at our discretion. Refunds for App Store / Play Store purchases are handled by Apple / Google respectively.</p>
    </Section>
    <Section n="03" title="Your content">
      <p>Your portraits, wardrobe items, outfits and shared looks remain yours. By marking an outfit as "public" you grant Atelier a non-exclusive licence to display it inside the app and to unauthenticated viewers of your share link.</p>
    </Section>
    <Section n="04" title="Acceptable use">
      <p>Don't upload another person's likeness without their permission. Don't upload illegal, hateful, or sexually explicit content. Don't attempt to reverse-engineer the AI models or scrape the service. We may suspend accounts that break these rules.</p>
    </Section>
    <Section n="05" title="Affiliate links">
      <p>Some links on Atelier are affiliate links — when you buy from partner retailers, we may receive a small commission at no extra cost to you. This helps keep the subscription price low.</p>
    </Section>
    <Section n="06" title="Third-party services">
      <p>By using Atelier you also agree to the terms of the payment and AI providers we use: Stripe, Apple / Google Play, Google Gemini, Anthropic Claude, and RevenueCat.</p>
    </Section>
    <Section n="07" title="Warranty &amp; liability">
      <p>Atelier is provided "as is" without warranties of any kind. Our maximum liability to you for any claim is limited to the amount you've paid us in the twelve months preceding the claim.</p>
    </Section>
    <Section n="08" title="Governing law">
      <p>These terms are governed by the laws of England &amp; Wales. Any dispute will be handled in the courts of England &amp; Wales. If you're a consumer resident elsewhere, your local mandatory consumer protections still apply.</p>
    </Section>
    <div className="pt-12 text-sm text-black/40 border-t border-black/10 mt-6">
      © {new Date().getFullYear()} Atelier. All rights reserved.
    </div>
  </div>
);

const Legal = () => null;
export default Legal;
